import React from 'react';
import { Link } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';

// layout
import AppLayout from '../../../layouts/AppLayout/AppLayout';

// components 
import { NioSection, NioButton, NioMedia, NioCard, NioSubscribeField, NioIcon } from '../../../components';

// section content 
import ContactForm from '../../../components/PageComponents/SectionComponents/ContactForm/ContactForm';
import { useTranslation } from '../../../i18n/I18nProvider';

function getStoredUserRole() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return '';
    const u = JSON.parse(raw);
    return String(u?.role || '').trim().toLowerCase();
  } catch {
    return '';
  }
}

function Contact() {
  const { t } = useTranslation();
  const isAdmin = getStoredUserRole() === 'admin';
  return (
    <AppLayout title={t('contactPage.title')} rootClass="layout-1">

      {/*  Contact Section Start  */}
      <NioSection className="pt-120 pt-lg-160" masks={["blur-1 left center"]}>
        <NioSection.Head alignX="center">
          <span className="d-inline-block fs-14 text-uppercase text-primary fw-semibold mb-2" >{t('contactPage.needSupport')}</span>
          <h2 >{t('contactPage.title')}</h2>
          <p className="fs-20" > {t('contactPage.desc')} </p>
        </NioSection.Head>
        <NioSection.Content>
          <Row className="gy-5 gy-xl-0">
            <Col md={6} xl={4} >
              <NioCard className="h-100 bg-blue-50 border-0">
                <NioCard.Body className="d-flex flex-column justify-content-between">
                  <div>
                    <NioMedia size="lg" rounded variant="primary" icon="cc-alt2-fill" className="mb-3 mb-md-5" />
                    <h4>{t('contactPage.billingPayments')}</h4>
                    <p className="line-clamp-3">{t('contactPage.billingDesc')}</p>
                    <NioButton href="/help-center" className="btn-link mt-3 text-primary" icon="arrow-right after" label={t('contactPage.seePricingQuestions')} />
                  </div>
                </NioCard.Body>
              </NioCard>
            </Col>
            <Col md={6} xl={4} >
              <NioCard className="h-100 bg-blue-50 border-0">
                <NioCard.Body className="d-flex flex-column justify-content-between">
                  <div>
                    <NioMedia size="lg" rounded variant="primary" icon="users-fill" className="mb-3 mb-md-5" />
                    <h4>{t('contactPage.usersCollab')}</h4>
                    <p className="line-clamp-3">{t('contactPage.usersDesc')}</p>
                    <NioButton href="/help-center" className="btn-link mt-3 text-primary" icon="arrow-right after" label={t('contactPage.allDocs')} />
                  </div>
                </NioCard.Body>
              </NioCard>
            </Col>
            {!isAdmin && (
              <Col md={6} xl={4}>
                <NioCard className="h-100 bg-blue-50 border-0" >
                  <NioCard.Body className="d-flex flex-column justify-content-between">
                    <div>
                      <NioMedia size="lg" rounded variant="primary" icon="help-fill" className="mb-3 mb-md-5" />
                      <h4>{t('contactPage.afterSales')}</h4>
                      <p className="line-clamp-3">{t('contactPage.afterSalesDesc')}</p>
                      <NioButton href="/support-sav" className="btn-link mt-3 text-primary" icon="arrow-right after" label={t('contactPage.openSavTicket')} />
                    </div>
                  </NioCard.Body>
                </NioCard>
              </Col>
            )}
          </Row>
        </NioSection.Content>
      </NioSection>
      {/*  Contact Section End  */}

      {/*  Support Section Start  */}
      <NioSection masks={["blur-1 right center"]}>
        <NioSection.Head alignX="center">
          <span className="d-inline-block fs-14 text-uppercase text-primary fw-semibold mb-2">{t('contactPage.contactSupport')}</span>
          <h2>{t('contactPage.getInTouch')}</h2>
          <p className="fs-20">{t('contactPage.getInTouchDesc')}</p>
        </NioSection.Head>
        <NioSection.Content>
          <Row className="gy-5">
            <Col lg={8} >
              <ContactForm />
            </Col>
            <Col lg={4}>
              <div className="card-list">
                <NioCard className="rounded-2">
                  <NioCard.Body>
                    <NioMedia size="lg" rounded variant="primary-soft" icon="sign-usd" className="mb-3 mb-md-5" />
                    <h4>{t('contactPage.plansPricing')}</h4>
                    <p className="line-clamp-2">{t('contactPage.plansDesc')}</p>
                    <div className="pt-2">
                      <Link to="/pricing" className="bg-transparent p-0 rounded-0 border-0 btn-link text-primary">
                        <span>{t('contactPage.seePricing')}</span>
                        <NioIcon name="arrow-right" />
                      </Link>
                    </div>
                  </NioCard.Body>
                </NioCard>
                <NioCard className="rounded-2">
                  <NioCard.Body>
                    <NioMedia size="lg" rounded variant="primary-soft" icon="question" className="mb-3 mb-md-5" />
                    <h4>{t('contactPage.faq')}</h4>
                    <p className="line-clamp-2">{t('contactPage.faqDesc')}</p>
                    <div className="pt-2">
                      <Link to="#" className="bg-transparent p-0 rounded-0 border-0 btn-link text-primary">
                        <span>{t('contactPage.seeAllQuestions')}</span>
                        <NioIcon name="arrow-right" />
                      </Link>
                    </div>
                  </NioCard.Body>
                </NioCard>
              </div>
              {/*  .card-list  */}
            </Col>
          </Row>
        </NioSection.Content>
      </NioSection>
      {/*  Support Section End  */}

      {/*  CTA Section Start  */}
      <NioSection>
        <NioSection.Content>
          <div className="nk-cta-wrap nk-cta-card bg-primary-gradient rounded-3 is-theme position-relative overflow-hidden" >
            <Row className="g-0 align-items-center overflow-hidden">
              <Col xs={{ order: 2 }} lg={{ span: 7, order: 0 }}>
                <div className="nk-block-head-content mb-0">
                  <h2 className="newsletter-heading text-capitalize h1 m-0 mb-4 mb-lg-7">{t('contactPage.ctaTitle')}</h2>
                  <ul className="nk-btn-group flex-wrap ps-1">
                    <li>
                      <NioButton href="#" className="btn-lg btn-white text-dark" label={t('contactPage.getStarted')} />
                    </li>
                    <li>
                      <NioButton href="/pricing" className="btn-lg btn-outline-white" label={t('contactPage.pricingPlans')} />
                    </li>
                  </ul>
                </div>
              </Col>
              <Col xs={{ order: 1 }} lg={{ span: 5, order: 0 }}>
                <div className="nk-cta-img-wrap text-end ps-5 pt-7 pt-lg-0 ps-sm-6 ps-lg-0">
                  <img src="images/thumb/c.png" alt="tracking-thumb" className="me-n1" />
                </div>
              </Col>
            </Row>
          </div>
        </NioSection.Content>
      </NioSection>
      {/*  CTA Section End  */}

      {/*  Newsletter Section Start  */}
      <NioSection className="nk-newsletter-section pb-lg-0">
        <Row className="justify-content-center justify-content-lg-between align-items-center pb-5 border-bottom border-lighter">
          <Col lg={6} xl={4}>
            <div className="nk-newsletter-content text-center text-lg-start pb-5 pb-lg-0">
              <h4 className="text-capitalize">{t('contactPage.newsletterTitle')}</h4>
              <p className="fs-16">{t('contactPage.newsletterDesc')}</p>
            </div>
          </Col>
          <Col md={10} lg={6} xl={5}>
            <NioSubscribeField variant="one" />
          </Col>
        </Row>
      </NioSection>
      {/*  Newsletter Section End  */}
    </AppLayout >
  )
}

export default Contact;